import { api, launchHref } from "../api";
import { el } from "../lib/dom";
import { accessBadge, errorBox, heroBand } from "../lib/ui";
import type { AppSummary } from "../types";

/** Brand SVG per app key; falls back to the app's initial if none is bundled. */
function appIcon(app: AppSummary, cls: string): HTMLElement {
  const img = el("img", { class: cls, src: `/icons/${app.key}.svg`, alt: "" });
  img.addEventListener("error", () =>
    img.replaceWith(
      el("span", { class: `${cls} tile-icon-fallback` }, [app.name[0]?.toUpperCase() ?? "?"]),
    ),
  );
  return img;
}

export async function renderHub(): Promise<HTMLElement> {
  const root = el("main", { class: "page" });

  // Full-width hero, then a two-column shell: favourites rail (left) + catalogue (right).
  root.append(
    heroBand(
      "Bounteous internal",
      "apps",
      "Pick an app, see what it does, then launch it.",
    ),
  );

  const railHost = el("aside", { class: "fav-rail" });
  const blogsHost = el("div", { class: "side-block" });
  const leftCol = el("div", { class: "home-side" }, [blogsHost, railHost]);
  const main = el("div", { class: "home-main" });
  root.append(el("div", { class: "home-layout" }, [leftCol, main]));

  const grid = el("div", { class: "app-grid" });
  const search = el("input", { class: "control-input", type: "search", placeholder: "Search apps…" });
  const typeSelect = el("select", { class: "control-input" }, [el("option", { value: "" }, ["All types"])]);
  main.append(
    el("div", { class: "controls" }, [
      el("label", { class: "control" }, [el("span", { class: "eyebrow" }, ["Search"]), search]),
      el("label", { class: "control" }, [el("span", { class: "eyebrow" }, ["Type"]), typeSelect]),
    ]),
    grid,
  );

  let apps: AppSummary[] = [];
  let favs: AppSummary[] = [];
  try {
    [apps, favs] = await Promise.all([api.listApps(), api.listFavourites()]);
  } catch {
    main.append(errorBox("Couldn't load the app catalogue. Try refreshing."));
    return root;
  }
  // Preview apps — staged, not yet published. The endpoint returns [] for anyone
  // who isn't a super-admin, so a non-admin simply never sees the section.
  let preview: AppSummary[] = [];
  try {
    preview = await api.listPreview();
  } catch {
    /* no preview section on error */
  }

  // Blogs is a read/write DESTINATION, not a launchable tool — pull it out of the
  // tile grid and render it as a distinct open-to-all block in the left column.
  const blogsApp = apps.find((a) => a.key === "blogs") ?? null;
  apps = apps.filter((a) => a.key !== "blogs");
  if (blogsApp) {
    blogsHost.append(
      el(
        "a",
        { class: "blogs-open", href: launchHref(blogsApp.key), target: "_blank", rel: "noopener" },
        [
          el("span", { class: "bi", "aria-hidden": "true" }, ["✍️"]),
          el("span", { class: "blogs-open-body" }, [
            el("span", { class: "blogs-tag" }, ["Open to all"]),
            el("div", { class: "blogs-open-title" }, [blogsApp.name]),
            el("div", { class: "blogs-open-sub" }, [blogsApp.tagline || "Read & publish"]),
          ]),
          el("span", { class: "blogs-open-arrow", "aria-hidden": "true" }, ["→"]),
        ],
      ),
    );
  }

  // listApps and listFavourites return *separate* objects for the same app.
  // Point the rail at the grid's instances so a toggle mutates one shared object
  // and both views stay in sync (otherwise unstarring in the rail leaves the
  // tile's star filled for a returning user).
  favs = favs.map((f) => apps.find((a) => a.key === f.key) ?? f);

  for (const t of [...new Set(apps.map((a) => a.app_type))].sort()) {
    typeSelect.append(el("option", { value: t }, [t]));
  }

  // --- favourites: optimistic toggle + persisted custom order ---------------

  async function toggleFavourite(app: AppSummary): Promise<void> {
    const next = !app.is_favourite;
    app.is_favourite = next;
    if (next) {
      if (!favs.some((f) => f.key === app.key)) favs.push(app);
    } else {
      favs = favs.filter((f) => f.key !== app.key);
    }
    paintRail();
    paintGrid();
    try {
      if (next) await api.addFavourite(app.key);
      else await api.removeFavourite(app.key);
    } catch {
      // Revert on failure so the UI never lies about server state.
      app.is_favourite = !next;
      favs = next ? favs.filter((f) => f.key !== app.key) : [...favs, app];
      paintRail();
      paintGrid();
    }
  }

  function railItem(app: AppSummary): HTMLElement {
    const item = el(
      "a",
      {
        class: "fav-item",
        href: launchHref(app.key),
        target: "_blank",
        rel: "noopener",
        draggable: "true",
        title: `Launch ${app.name}`,
        dataset: { favKey: app.key },
      },
      [
        el("span", { class: "fav-grip", "aria-hidden": "true" }, ["⋮⋮"]),
        appIcon(app, "fav-icon"),
        el("span", { class: "fav-name" }, [app.name]),
        el(
          "button",
          {
            class: "fav-remove",
            type: "button",
            "aria-label": `Remove ${app.name} from favourites`,
            title: "Remove from favourites",
            onclick: (e: Event) => {
              e.preventDefault();
              e.stopPropagation();
              void toggleFavourite(app);
            },
          },
          ["★"],
        ),
      ],
    );
    return item;
  }

  function paintRail(): void {
    railHost.replaceChildren(
      el("h2", { class: "fav-title" }, ["Favourites"]),
      favs.length
        ? el("div", { class: "fav-list" }, favs.map(railItem))
        : el("p", { class: "fav-empty" }, ["Star an app you have access to for one-click launch."]),
    );
    if (favs.length) enableDragReorder(railHost.querySelector(".fav-list") as HTMLElement);
  }

  function enableDragReorder(list: HTMLElement): void {
    let dragging: HTMLElement | null = null;

    const after = (y: number): HTMLElement | null => {
      const items = [...list.querySelectorAll<HTMLElement>(".fav-item:not(.dragging)")];
      let closest: { offset: number; el: HTMLElement | null } = {
        offset: Number.NEGATIVE_INFINITY,
        el: null,
      };
      for (const child of items) {
        const box = child.getBoundingClientRect();
        const offset = y - box.top - box.height / 2;
        if (offset < 0 && offset > closest.offset) closest = { offset, el: child };
      }
      return closest.el;
    };

    list.addEventListener("dragstart", (e) => {
      const item = (e.target as HTMLElement).closest<HTMLElement>(".fav-item");
      if (!item) return;
      dragging = item;
      item.classList.add("dragging");
      e.dataTransfer?.setData("text/plain", item.dataset.favKey ?? "");
      if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
    });

    list.addEventListener("dragover", (e) => {
      if (!dragging) return;
      e.preventDefault(); // allow drop
      const ref = after(e.clientY);
      if (ref == null) list.appendChild(dragging);
      else list.insertBefore(dragging, ref);
    });

    list.addEventListener("dragend", () => {
      if (!dragging) return;
      dragging.classList.remove("dragging");
      dragging = null;
      const order = [...list.querySelectorAll<HTMLElement>(".fav-item")].map(
        (n) => n.dataset.favKey ?? "",
      );
      favs = order.map((k) => favs.find((f) => f.key === k)!).filter(Boolean);
      void api.reorderFavourites(order).catch(() => {});
    });
  }

  // --- catalogue tiles ------------------------------------------------------

  function tileActions(app: AppSummary): HTMLElement {
    // View Details always available; the second action depends on access.
    const details = el(
      "a",
      { class: "tile-btn", href: `/apps/${app.key}`, "data-link": "" },
      ["View Details"],
    );
    // Under maintenance: no launch / no request — just the detail page (which explains).
    if (app.maintenance) {
      return el("div", { class: "tile-actions" }, [
        details,
        el("button", { class: "tile-btn", type: "button", disabled: "" }, ["Under maintenance"]),
      ]);
    }
    if (app.access_status === "granted") {
      // Credentials apps have no SSO handoff — the login lives on the detail page.
      // Send people there (not into the app's login wall) so they get the username/
      // password to copy before launching.
      if (app.launch_strategy === "credentials") {
        return el("div", { class: "tile-actions" }, [
          el(
            "a",
            { class: "tile-btn is-primary", href: `/apps/${app.key}`, "data-link": "" },
            ["View Details"],
          ),
        ]);
      }
      // Direct launch from the tile (same full-navigation path as favourites).
      const launch = el(
        "a",
        { class: "tile-btn is-primary", href: launchHref(app.key), target: "_blank", rel: "noopener" },
        ["Launch App"],
      );
      return el("div", { class: "tile-actions" }, [details, launch]);
    }
    if (app.access_status === "pending") {
      return el("div", { class: "tile-actions" }, [
        details,
        el("button", { class: "tile-btn", type: "button", disabled: "" }, ["Requested"]),
      ]);
    }
    // Governed app: no self-serve request — the detail page explains how to get access.
    if (app.self_request === false) {
      return el("div", { class: "tile-actions" }, [
        details,
        el(
          "button",
          {
            class: "tile-btn",
            type: "button",
            disabled: "",
            title: app.access_note || "Access is granted by an administrator.",
          },
          ["By approval"],
        ),
      ]);
    }
    // Reason is mandatory, so route to the detail page to enter it (no instant request).
    const request = el(
      "a",
      { class: "tile-btn is-primary", href: `/apps/${app.key}`, "data-link": "" },
      ["Request Access"],
    );
    return el("div", { class: "tile-actions" }, [details, request]);
  }

  function tile(app: AppSummary): HTMLElement {
    const children: HTMLElement[] = [
      el("div", { class: "tile-head" }, [
        appIcon(app, "tile-icon"),
        el("div", {}, [
          el("span", { class: "tile-name" }, [app.name]),
          el("span", { class: "tile-type" }, [app.app_type]),
        ]),
      ]),
      el("p", { class: "tile-desc" }, [app.tagline || app.description]),
      el("div", { class: "tile-foot" }, [accessBadge(app.access_status)]),
      tileActions(app),
    ];
    // Surface a failed in-app provisioning so the user knows why the app itself
    // might still bounce them (Hub access is fine; retrying in the background).
    if (app.access_status === "granted" && app.provision_status === "failed") {
      children.push(
        el("div", { class: "tile-prov-warn" }, [
          "In-app access setup failed — we're retrying. Launch may show the app's own access screen meanwhile.",
        ]),
      );
    }
    const card = el("div", { class: "app-tile", dataset: { type: app.app_type } }, children);
    // Star toggle — only granted apps can be favourited (it's a launch shortcut).
    if (app.access_status === "granted") {
      card.append(
        el(
          "button",
          {
            class: `tile-star${app.is_favourite ? " is-on" : ""}`,
            type: "button",
            "aria-pressed": String(!!app.is_favourite),
            "aria-label": app.is_favourite ? "Remove from favourites" : "Add to favourites",
            title: app.is_favourite ? "Remove from favourites" : "Add to favourites",
            onclick: (e: Event) => {
              e.preventDefault();
              e.stopPropagation();
              void toggleFavourite(app);
            },
          },
          [app.is_favourite ? "★" : "☆"],
        ),
      );
    }
    return card;
  }

  // Show apps the user can actually use first: granted → pending → no access.
  // (Stable sort preserves the catalogue's sort_order within each group.)
  const accessRank = (a: AppSummary): number =>
    a.access_status === "granted" ? 0 : a.access_status === "pending" ? 1 : 2;

  function paintGrid(): void {
    const q = search.value.trim().toLowerCase();
    const type = typeSelect.value;
    const filtered = apps
      .filter(
        (a) =>
          (!type || a.app_type === type) &&
          (!q || `${a.name} ${a.description}`.toLowerCase().includes(q)),
      )
      .sort((a, b) => accessRank(a) - accessRank(b));
    grid.replaceChildren(
      ...(filtered.length
        ? filtered.map(tile)
        : [el("p", { class: "muted" }, ["No apps match your filters."])]),
    );
  }

  search.addEventListener("input", paintGrid);
  typeSelect.addEventListener("change", paintGrid);
  paintRail();
  paintGrid();

  // --- Preview section (super-admins only) ----------------------------------
  // A bounded block at the bottom holding staged apps. Each tile routes to its
  // detail page, where a super-admin can Publish it to everyone.
  if (preview.length) {
    main.append(
      el("section", { class: "preview-section" }, [
        el("div", { class: "preview-head" }, [
          el("span", { class: "preview-badge" }, ["Preview"]),
          el("span", { class: "muted-sm" }, [
            "Early access — not published yet. Only people granted an app here can see it; open one for details.",
          ]),
        ]),
        el("div", { class: "app-grid" }, preview.map(tile)),
      ]),
    );
  }
  return root;
}
